import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
  Box,
  Button,
  DataTableToolbar,
  DataTableToolbarActions,
  DataTableToolbarLeft,
  Icon,
  Separator,
  useIsMobile,
} from "@salestrak/ui";

import { ViewSearch } from "#app/components/filters/view-search";
import { PageHeader } from "#app/components/page-header";

import {
  TeamsTable,
  buildTeamsFilterDefs,
  useTeamsColumns,
} from "#app/components/teams/teams-table";
import { CreateTeamDialog } from "#app/components/teams/create-team-dialog";
import { useListQueryState } from "#app/lib/hooks/use-list-query-state";
import { TEAM_LIST } from "#app/lib/filters/list-configs";
import { useTeamMutations } from "#app/lib/hooks/use-team-mutations";
import { useAppData } from "#app/lib/data";
import { useUserPool } from "#app/components/entity-selectors/hooks/use-user-pool";
import {
  MemberQuickFiltersDivider,
} from "#app/components/members/member-quick-filters";
import {
  TeamQuickFilters,
  TeamSortChip,
} from "#app/components/teams/team-quick-filters";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { usePermission } from "#app/lib/hooks/use-permission";
import { useTableFilters } from "#app/lib/hooks/use-table-filters";
import { useTableViewSettings } from "#app/lib/hooks/use-table-view-settings";
import { useAnalytics, AnalyticsContext, TeamEvent, useTrackSearch } from "#app/lib/analytics";
import { withMemberModule } from "#app/components/permissions/require-permission";

function TeamsScreen() {
  const { push } = useAppNavigate();
  const { track } = useAnalytics();
  const { currentUser, refetch: refetchAppData } = useAppData();
  const { removeTeamMembers } = useTeamMutations();
  const isMobile = useIsMobile();
  const canManageTeams = usePermission("manage.teams");

  // The chips and the filter defs both page and search the server through
  // the one pool.
  const { pool: userPool } = useUserPool();
  const filterDefs = useMemo(
    () => buildTeamsFilterDefs(userPool),
    [userPool],
  );
  const teamFilters = useTableFilters(filterDefs);

  const { searchValue, setSearchValue, sorting, setSorting, where, orderBy } =
    useListQueryState(TEAM_LIST, { filterWhere: teamFilters.where });
  useTrackSearch(searchValue, () => ({
    context: AnalyticsContext.Team,
    event: TeamEvent.Search,
  }));

  // Dialog state
  const [leaveTeamId, setLeaveTeamId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  // Truly empty — zero teams with no search or filter narrowing the list.
  // The toolbar (filter/search/columns) has nothing to operate on, so it
  // yields to a lone create action in the title row. A zero-result search
  // or filter keeps the toolbar: the user needs it to widen the view again.
  const [teamTotal, setTeamTotal] = useState<number | undefined>(undefined);
  const isTrulyEmpty =
    teamTotal === 0 && !searchValue && teamFilters.activeFilterCount === 0;

  // Post-create: refresh the sidebar's team list, then land on the new
  // team's own page (members are added on its roster tab). Creation lives
  // HERE so the user never has to detour through workspace settings to make
  // a team — and landing in settings afterwards would be that same detour.
  // Only the settings list's create lands in settings.
  const handleTeamCreated = useCallback(
    async (teamId: string) => {
      await refetchAppData();
      push(`/team/${teamId}`);
    },
    [refetchAppData, push],
  );

  // Clicking a team navigates to its page — same destination as the
  // left-hand "Teams" menu (`/team/[teamId]`).
  const handleViewTeam = useCallback(
    (teamId: string) => {
      track({ context: AnalyticsContext.Team, event: TeamEvent.Open, data: { target: "page" } });
      push(`/team/${teamId}`);
    },
    [push, track],
  );

  // Row-menu "Team settings" — the manage page under workspace settings,
  // not the team dashboard. Gated the same as the header's "Manage teams"
  // button; without the permission the menu item is hidden entirely.
  const handleTeamSettings = useCallback(
    (teamId: string) => {
      push(`/settings/teams/${teamId}`);
    },
    [push],
  );

  const handleLeaveTeam = useCallback((teamId: string) => {
    setLeaveTeamId(teamId);
  }, []);

  // The Members cell is a door to the roster tab: the four-face preview
  // cannot show everyone, so pressing it takes you to where they all are.
  const handleViewTeamMembers = useCallback(
    (teamId: string) => {
      push(`/team/${teamId}/members`);
    },
    [push],
  );

  const columns = useTeamsColumns({
    variant: "app",
    onViewTeam: handleViewTeam,
    onViewTeamMembers: handleViewTeamMembers,
  });
  const tableView = useTableViewSettings("teams:app");

  const handleConfirmLeave = useCallback(async () => {
    if (!leaveTeamId || !currentUser?.userId) return;
    // Distinct from the hook's generic Settings/remove_members: this captures
    // leaving your own team from the teams list as a discrete user action.
    const result = await removeTeamMembers(leaveTeamId, [currentUser.userId]);
    if (result) {
      track({ context: AnalyticsContext.Team, event: TeamEvent.Leave, data: { via: "teams_list" } });
    }
    setLeaveTeamId(null);
  }, [leaveTeamId, currentUser?.userId, removeTeamMembers, track]);

  return (
    <Box direction="column" fill="flex">
      <PageHeader
        title="All Teams"
        actions={
          // Header-resident create action, same placement as the all-members
          // view's Invite button.
          canManageTeams ? (
            <Button
              size={isMobile ? "icon-xs" : "xs"}
              onClick={() => setCreateOpen(true)}
            >
              {/* Raw string: a bare Text element pins 14px into the 24px
                * rung; a string child inherits the button's own 12px font. */}
              {isMobile ? <Icon name="plus" /> : "Create Team"}
            </Button>
          ) : undefined
        }
      />
      <Separator />
      {!isTrulyEmpty && (
        <DataTableToolbar>
          <DataTableToolbarLeft>
            {/* The members directory's chip grammar: one labeled chip per
                facet, then Sort past a hairline — all one chip species, so
                the row reads as one set of controls. */}
            <TeamQuickFilters
              filters={teamFilters}
              memberPool={userPool}
            />
            <MemberQuickFiltersDivider />
            <TeamSortChip sorting={sorting} onSortingChange={setSorting} />
          </DataTableToolbarLeft>
          <DataTableToolbarActions>
            <ViewSearch
              onValueChange={setSearchValue}
              size="xs"
            />
          </DataTableToolbarActions>
        </DataTableToolbar>
      )}
      <TeamsTable
        variant="app"
        gutterTop="md"
        where={where}
        orderBy={orderBy}
        sorting={sorting}
        onSortingChange={setSorting}
        searchValue={searchValue}
        filtered={teamFilters.activeFilterCount > 0}
        columns={columns}
        tableViewProps={tableView.tableProps}
        // Actions-column circle-plus: the built-in Show columns / Available
        // picker.
        onColumnVisibilityChange={tableView.settingsProps.onColumnVisibilityChange}
        onNavigateToTeam={handleTeamSettings}
        onLeaveTeam={handleLeaveTeam}
        // Same gate as the header button — one permission, both affordances.
        onCreateTeam={canManageTeams ? () => setCreateOpen(true) : undefined}
        onTotalChange={setTeamTotal}
      />
      <CreateTeamDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={handleTeamCreated}
      />
      <AlertDialog
        open={!!leaveTeamId}
        onOpenChange={(open) => { if (!open) setLeaveTeamId(null); }}
      >
        <AlertDialogPopup>
          <AlertDialogHeader>
            <AlertDialogTitle>Leave Team?</AlertDialogTitle>
            <AlertDialogDescription>
              You will be removed from this team. You can be re-added later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogClose render={<Button variant="outline" />}>
              Cancel
            </AlertDialogClose>
            <Button color="destructive" onClick={handleConfirmLeave}>
              Leave team
            </Button>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>
    </Box>
  );
}

export const Route = createFileRoute("/(app)/$workspace/teams")({
  // A member module: a Guest never reaches it (`ModuleTier`). The rail
  // omits the tile; the route denies for itself since the URL still resolves.
  component: withMemberModule(TeamsScreen),
});
